<template>
  <main :aria-label="$t('general.user_list')" class="users-list-page">
    <CompanyBreadcrumb :aria-label="$t('general.user_list')"
                       :home-aria-label="$t('general.home')"
                       :items="breadcrumbItems"
    />

    <div v-if="successMessage" class="card users-alert-card">
      <div class="uk-alert-success" uk-alert>
        <a class="uk-alert-close" uk-close></a>
        <p>
          <i class="uil-check"></i>
          {{ successMessageText }}
        </p>
      </div>
    </div>

    <div class="section-header mb-lg-2 border-0 uk-flex-middle users-page-header">
      <div class="section-header-left">
        <h2 class="users-page-title">
          <span>{{ $t("users.users") }}</span>
        </h2>
      </div>
      <div class="section-header-right">
        <button :disabled="!$hasPermission('create_user')"
                :style="{ cursor: !$hasPermission('create_user') ? 'not-allowed' : 'pointer' }"
                class="btn btn-default users-add-btn"
                type="button"
                @click="$router.push({ name: 'company.user.add' })">
          <i class="uil-plus"></i> {{ $t("users.add_user") }}
        </button>
      </div>
    </div>
    <div class="card users-list-card">
      <!-- Card header -->
      <div class="card-header actions-toolbar border-0 users-toolbar">
        <div class="d-flex justify-content-between users-toolbar-inner">
          <!-- Parantez SART. CompanySearchInput `input`'u degerle yayiyor
               ($emit('input', $event.target.value)). Parantezsiz baglanirsa
               o string filterResult'a "filtre nesnesi" olarak gecer,
               keepFilter'i bozar ve buildRequestFilters'ta patlar. -->
          <CompanySearchInput v-model="filter.search"
                              :placeholder="$t('general.search')"
                              class="users-toolbar-search"
                              @input="debouncedFilterSearch()"
          />
          <div class="d-flex users-toolbar-actions">
            <multiple-select-input v-model="selectedPerPage"
                                   :allow-empty="false"
                                   :control-height="48"
                                   :min-width="110"
                                   :multiple="false"
                                   :searchable="false"
                                   :show-labels="false"
                                   :values="perPageOptions"
                                   class="users-per-page-select"
            />
            <sort-dropdown :sort-options="sortOptions" @sort-selected="sortOrder"/>

            <!-- Filter -->
            <a :aria-label="$t('general.filter_list')"
               :uk-tooltip="$t('general.filter_list')"
               class="btn btn-icon btn-hover btn-circle users-icon-btn"
               href="javascript:void(0)">
              <i class="uil-filter"></i>
            </a>
            <UserFilter :has-status="true" :show-integration-filter="true" :show-role-filter="true"
                        @getFilter="getFilter($event)"></UserFilter>

            <excel-download-button :loading="isReportDownloading" :tooltipText="$t('filters.export_excell')"
                                   @download="exportUsers"/>

          </div>
        </div>
      </div>
      <!-- Table -->
      <default-loading v-if="isFetchingList"></default-loading>
      <template v-else>
        <div class="users-summary">
          <div class="users-summary-stats">
            <span>{{ $t('general.active_users') }} : {{
                counts.active_users_count
              }}</span>
            <span>{{ $t('general.inactive_users') }} : {{
                counts.passive_users_count
              }}</span>
            <span>{{ $t('general.Total Users') }} : {{
                counts.total_users_count
              }}</span>
          </div>
          <div class="users-summary-selected">
            {{ $t('general.number_of_selected_users') }}: <i class="uil-users-alt"></i>
            {{ selectedUsersCount }}
          </div>
        </div>
        <div class="d-flex disabled_hover users-bulk-actions">
          <div class="users-bulk-actions-left">
            <button class="btn btn-sm btn-success" type="button" @click="sendForgotPasswordMail()">
              {{ $t('general.send_forgot_password_mail') }}
            </button>
            <button class="btn btn-sm btn-success" type="button" @click="sendWelcomeMail()">
              {{ $t('general.send_welcome_mail') }}
            </button>
            <button class="btn btn-sm btn-success" type="button" @click="activeSelectedUsers()">
              <i class="uil-check"></i> {{ $t('general.activate_selected_users') }}
            </button>
            <button class="btn btn-sm btn-danger" type="button" @click="passiveSelectedUsers()">
              <i class="uil-minus-circle"></i> {{ $t('general.inactive_selected_users') }}
            </button>
          </div>
          <div class="users-bulk-actions-right">
            <button class="btn btn-sm btn-secondary" type="button" @click="allUserToggle()">
              <i class="uil-check-square"></i> {{ selectAllUserText }}
            </button>
            <button v-if="$hasPermission('delete_user')" class="btn btn-sm btn-secondary" type="button"
                    @click="deleteSelectedUsers()">
              <i class="uil-trash"></i> {{ $t('general.delete_selected_users') }}
            </button>
            <button v-else :style="{
            cursor: 'not-allowed',

          }" :uk-tooltip="$t('general.no_permission')" class="btn btn-sm btn-secondary" disabled type="button">
              <i class="uil-trash"></i> {{ $t('general.delete_selected_users') }}
            </button>
          </div>
        </div>
        <div class="sf-table sf-table--zebra sf-table--hover users-table-wrap">
          <table>
            <thead>
            <tr>
              <th class="sf-table__col-check" scope="col"></th>
              <th scope="col">
                {{ $t("general.username") }}
              </th>
              <th scope="col">{{ $t("users.title") }}</th>
              <th v-if="!shouldHideEmailField" scope="col">{{ $t("general.email") }}</th>
              <th scope="col">{{ $t('general.status') }}</th>
              <th scope="col">{{ $t("general.group_info") }}</th>
              <th scope="col">{{ $t("users.institution") }}</th>
              <th scope="col">{{ $t("general.created_at") }}</th>
              <th scope="col">{{ $t("users.last_login_date") }}</th>
              <th scope="col">{{ $t("general.phone") }}</th>
              <th class="sf-table__col-actions" scope="col"></th>
            </tr>
            </thead>
            <tbody class="list">
            <template v-for="item in formattedItem">
              <tr :key="item.id">
                <th class="sf-table__col-check">
                  <label :for="'notification_checkbox_' + item.id" class="sf-table__check">
                    <input :id="'notification_checkbox_' + item.id" v-model="item_checked" :value="item.id"
                           type="checkbox"/>
                  </label>
                </th>
                <th scope="row">
                  <div class="sf-table__media">
                    <div>
                      <div class="avatar-parent-child">
                        <AvatarOrIcon :size-px="32" :src="item.photo_link"/>
                        <!-- Rozet CEVRIMICI durumunu gosterir, aktif/pasif'i degil.
                             Aktif/pasif zaten "Durum" kolonunda yazili. -->
                        <span :class="item.is_online ? 'bg-success' : 'bg-offline'"
                              class="avatar-child avatar-badge"></span>
                      </div>
                    </div>
                    <div class="sf-table__media-body">
                      <a class="sf-table__name" @click="openProfileModal(item)">
                        {{ item.fullname }}
                      </a>
                      <small class="sf-table__meta">#{{ item.id }}</small>
                    </div>
                  </div>
                </th>
                <td>{{ item.title }}</td>
                <td v-if="!shouldHideEmailField">{{ item.email }}</td>
                <td :class="getUserStatus(item.status_id).className">{{ getUserStatus(item.status_id).text }}</td>
                <td class="sf-table__wrap">
                  <span v-for="(group, index) in item.group_info" :key="index"
                        class="sf-table__chip sf-table__chip--success">
                    {{ group.name }}
                  </span>
                </td>
                <td>{{
                    item.corporate_id == null || !item.corporate_name
                        ? item.company_name
                        : item.company_name + ' / ' + item.corporate_name
                  }}
                </td>
                <td>{{ item.created_at }}</td>
                <td>{{ item.last_login_at }}</td>
                <td>{{ item.phone }}</td>
                <td class="sf-table__col-actions">
                  <!-- Actions -->
                  <div class="sf-table__actions">
                    <a :uk-tooltip="$t('general.user_profil')" class="btn btn-icon btn-hover btn-sm btn-circle"
                       @click="openProfileModal(item)">
                      <i class="uil-user"></i>
                    </a>
                    <a v-if="$isSuperAdmin" :uk-tooltip="$t('general.user_account_login')"
                       class="btn btn-icon btn-hover btn-sm btn-circle" @click="openUserAccount(item)">
                      <i class="uil-eye "></i>
                    </a>
                    <a v-if="item.status_id !== 0" :style="{
                        cursor: $hasPermission('create_assignment') ? 'pointer' : 'not-allowed',
                        opacity: $hasPermission('create_assignment') ? 1 : 0.5
                      }" :uk-tooltip="$hasPermission('create_assignment')
  ? $t('general.assign_user_to_training')
  : $t('general.no_permission')" class="btn btn-icon btn-hover btn-sm btn-circle" type="button"
                       @click="$hasPermission('create_assignment') && openAssignmentModal(item.id)">
                      <i class="uil-location-arrow text-success"></i>
                    </a>

                    <a v-if="item.status_id !== 0" :uk-tooltip="$t('general.assign_to_group')"
                       class="btn btn-icon btn-hover btn-sm btn-circle" type="button" @click="openGroupModal(item.id)">
                      <i class="uil-user-square"></i>
                    </a>
                    <a v-if="item.status_id === 0" :uk-tooltip="$t('general.set_active')" aria-expanded="false"
                       class="btn btn-icon btn-hover btn-sm btn-circle" title="" @click="statusChange(item.id, 0)">
                      <i class="uil-minus-circle text-danger"></i>
                    </a>
                    <a v-else :uk-tooltip="$t('general.set_passive')" aria-expanded="false"
                       class="btn btn-icon btn-hover btn-sm btn-circle" title="" @click="statusChange(item.id, 1)">
                      <i class="uil-check text-success"></i>
                    </a>
                    <a :style="{
                        cursor: $hasPermission('delete_user') ? 'pointer' : 'not-allowed',
                        opacity: $hasPermission('delete_user') ? 1 : 0.5
                      }" :uk-tooltip="$hasPermission('delete_user')
  ? $t('collectionDetail.remove')
  : $t('general.no_permission')" aria-expanded="false" class="btn btn-icon btn-hover btn-sm btn-circle"
                       @click="$hasPermission('delete_user') && deleteUser(item.id)">
                      <i class="uil-trash text-danger"></i>
                    </a>

                    <a v-if="$isSuperAdmin" :uk-tooltip="$t('general.permissions')" aria-expanded="false"
                       class="btn btn-icon btn-hover btn-sm btn-circle" title="" @click="openPermissionModal()">
                      <i class="uil-key-skeleton"></i>
                    </a>

                  </div>
                </td>
              </tr>
            </template>
            </tbody>
          </table>
        </div>
        <div id="permission_modal" class="uk-modal" uk-modal>
          <div class="uk-modal-dialog bg-gray modal-container users-permission-dialog">
            <div class="uk-padding-small uk-grid">
              <h2>{{ $t('general.permissions') }}</h2>
            </div>
            <div class="col-md-12">
              <h4>{{ $t('general.role_definition') }}</h4>
            </div>
            <div class="col-md-12 users-permission-body">
              <h4>{{ $t('general.permissions') }}</h4>
              <div class="sf-table sf-table--compact sf-table--head-fill users-permission-table">
                <table>
                  <thead>
                  <tr>
                    <th></th>
                    <th class="sf-table__center">{{ $t('general.viewing') }}</th>
                    <th class="sf-table__center">{{ $t('general.add_action') }}</th>
                    <th class="sf-table__center">{{ $t('general.update_action') }}</th>
                    <th class="sf-table__center">{{ $t('general.delete_action') }}</th>
                  </tr>
                  </thead>
                  <tbody>
                  <tr v-for="(permission, index) in filteredPermissions" :key="index">
                    <td>
                      <label>
                        {{ permission.name }}
                      </label>
                    </td>
                    <td class="sf-table__center">
                      <i v-if="permission.actions.view" class="uil-key-skeleton"></i>
                      <i v-if="!permission.actions.view" class="uil-times"></i>
                    </td>
                    <td class="sf-table__center">
                      <i v-if="permission.actions.add" class="uil-key-skeleton"></i>
                      <i v-if="!permission.actions.add" class="uil-times"></i>
                    </td>
                    <td class="sf-table__center">
                      <i v-if="permission.actions.update" class="uil-key-skeleton"></i>
                      <i v-if="!permission.actions.update" class="uil-times"></i>
                    </td>
                    <td class="sf-table__center">
                      <i v-if="permission.actions.delete" class="uil-key-skeleton"></i>
                      <i v-if="!permission.actions.delete" class="uil-times"></i>
                    </td>
                  </tr>
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        </div>

        <pagination :pagination="pagination" class="mt-2 mb-3 users-pagination" @changePage="setPage($event)"/>
      </template>
    </div>

    <AssignmentSelectionModal :assignment-data="assignments"
                              :loading="assignment_loading"
                              :visible.sync="assignmentModalOpen"
                              @assignment-selected="assignmentCreate"
                              @close="onAssignmentModalClose"
                              @page-changed="handleAssignmentPageChange"
                              @search-changed="handleAssignmentSearch"/>

    <UserGroupModal :groups="groups"
                    :loading="group_loading"
                    :visible.sync="groupModalOpen"
                    @close="onGroupModalClose"
                    @group-selected="groupCreate"/>

    <MailRecipientScopeModal ref="mailScopeModal"
                             :mail-type-text="pendingMailTypeText"
                             :selected-count="selectedUsersCount"
                             @scope-selected="handleMailScopeSelected"/>

    <UserProfile v-if="activeItem && profileModalOpen" :key="`user-profile-${activeItem.id}-${profileRequestId}`"
                 :item="activeItem" :loading="isLoadingUserDetail || !resolvedUserDetail"
                 :user_detail="resolvedUserDetail"
                 @assignmentPageChanged="handleUserAssignmentPageChange" @modalClosed="closeModal"
                 @refresh="getUserDetail"/>

  </main>
</template>

<script>
import SecureLS from "secure-ls";

const ls = new SecureLS({ encodingType: "aes", isCompression: true });
import module, {
  BASE_URL,
  GET_ITEMS,
  GET_COUNTS,
  COUNTS,
  ITEMS,
  MODULE_NAME,
  SUCCESS,
  UPDATE_USER,
} from "@/core/services/store/user.module";

import user_detail_module, {
  GET_ITEM as GET_USER_DETAIL,
  ITEM as USER_DETAIL,
  MODULE_NAME as USER_DETAIL_MODULE,
} from "@/core/services/store/user_detail.module";

import assignment_module, {
  GET_ITEM as GET_ASSIGNMENT,
  ITEM as ASSIGNMENT_ITEM,
  MODULE_NAME as ASSIGNMENT_MODULE,
} from "@/core/services/store/assignment.module";

import group_module, {
  GET_ITEM as GET_GROUP,
  ITEM as GROUP_ITEM,
  MODULE_NAME as GROUP_MODULE,
} from "@/core/services/store/user_group.module";

import store from "@/core/services";
import moment from "moment";
import axios from "axios";
import Pagination from "@/assets/components/Pagination.vue";
import UserProfile from "@/view/company/User/UserProfile/index.vue";
import UserFilter from "@/view/company/User/Filter.vue";
import DefaultLoading from "@/view/components/loading/DefaultLoading.vue";
import company_module, {
  ONLINE_USERS,
  MODULE_NAME as COMPANY_MODULE,
} from "@/core/services/store/company.module";
import { confirmDialogDelete } from "@/utils/confirmDialog";
import AvatarOrIcon from "@/view/components/icons/AvatarOrIcon.vue";
import ExcelDownloadButton from "@/view/components/buttons/ExcelDownloadButton.vue";
import AssignmentSelectionModal from "@/view/company/User/components/AssignmentSelectionModal.vue";
import UserGroupModal from "@/view/company/User/components/UserGroupModal.vue";
import CompanyBreadcrumb from "@/view/company/components/breadcrumb/CompanyBreadcrumb.vue";
import CompanySearchInput from "@/view/company/components/search/CompanySearchInput.vue";
import SortDropdown from "@/view/components/dropdown/SortDropdown.vue";
import MultipleSelectInput from "@/assets/components/inputs/MultipleSelectInput.vue";
import debounce from "lodash/debounce";
import MailRecipientScopeModal from "@/view/User/components/MailRecipientScopeModal.vue";
import { showError } from "@/utils/alertUtils";
import { downloadFromRequest, exportFileName, readBlobError } from "@/utils/fileDownload";
import {
  getAssignedCorporateAccess,
  applyAssignedCorporateToFilter
} from "@/core/services/userCorporateAccess.service";

const _USER_DETAIL_MODULE = USER_DETAIL_MODULE;
const _MODULE_NAME = MODULE_NAME;
const _ASSIGNMENT_MODULE = ASSIGNMENT_MODULE;
const _GROUP_MODULE = GROUP_MODULE;
const _COMPANY_MODULE = COMPANY_MODULE;

export default {
  name: "Users",
  beforeCreate() {
    function registerStoreModule(moduleName, storeModule) {
      if (!(store && store.state && store.state[moduleName])) {
        store.registerModule(moduleName, storeModule);
      }
    }

    registerStoreModule(_MODULE_NAME, module);
    registerStoreModule(_USER_DETAIL_MODULE, user_detail_module);
    registerStoreModule(_ASSIGNMENT_MODULE, assignment_module);
    registerStoreModule(_GROUP_MODULE, group_module);
    registerStoreModule(_COMPANY_MODULE, company_module);
  },
  components: {
    ExcelDownloadButton,
    Pagination,
    UserProfile,
    UserFilter,
    DefaultLoading,
    AvatarOrIcon,
    AssignmentSelectionModal,
    UserGroupModal,
    CompanyBreadcrumb,
    CompanySearchInput,
    SortDropdown,
    MultipleSelectInput,
    MailRecipientScopeModal,
  },
  created() {
    this.debouncedFilterSearch = debounce(this.filterResult, 500);
  },
  data() {
    return {
      isLoadingUserDetail: false,
      filteredPermissions: [
        {
          name: this.$t("users.users"),
          enabled: true,
          actions: {
            view: true,
            create: false,
            update: false,
            delete: false
          }
        },
        {
          name: this.$t("general.contents"),
          enabled: false,
          actions: {
            view: false,
            create: false,
            update: false,
            delete: false
          }
        },
        {
          name: this.$t("general.tags"),
          enabled: false,
          actions: {
            view: false,
            create: false,
            update: false,
            delete: false
          }
        },
        {
          name: this.$t("general.programs"),
          enabled: false,
          actions: {
            view: false,
            create: false,
            update: false,
            delete: false
          }
        },
        {
          name: this.$t("general.assignments"),
          enabled: false,
          actions: {
            view: false,
            create: false,
            update: false,
            delete: false
          }
        },
        {
          name: this.$t("general.catalog_trainings"),
          enabled: false,
          actions: {
            view: false,
            create: false,
            update: false,
            delete: false
          }
        },
        {
          name: this.$t("general.surveys"),
          enabled: false,
          actions: {
            view: false,
            create: false,
            update: false,
            delete: false
          }
        },
        {
          name: this.$t("exam.exams"),
          enabled: false,
          actions: {
            view: false,
            create: false,
            update: false,
            delete: false
          }
        }
      ],
      pagination: null,
      activeItem: null,
      activeUser: null,
      selectAllUserText: this.$t("general.select_all"),
      per_page: 10,
      filter: {
        search: "",
        order: "desc",
        sort: "id",
        company_id: null,
      },
      assignment_loading: null,
      group_loading: null,
      successMessage: "",
      item_checked: [],
      temp_page: [],
      checked_user_page: [],
      keepFilter: null,
      isReportDownloading: false,
      assignmentSearchQuery: '',
      assignmentModalOpen: false,
      groupModalOpen: false,
      profileModalOpen: false,
      profileDetailUserId: null,
      profileRequestId: 0,
      isFetchingList: true,
      listRequestId: 0,
      // Kapsam modalında onay bekleyen mail tipi: 'forgot_password' | 'welcome'
      pendingMailType: null
    };
  },
  computed: {
    breadcrumbItems() {
      return [
        { name: this.$t("users.users") },
        { name: this.$t("general.user_list") },
      ];
    },
    sortOptions() {
      return [
        { key: "text-asc", sort: "name", order: "asc", label: this.$t("sorting.a_z") },
        { key: "text-desc", sort: "name", order: "desc", label: this.$t("sorting.z_a") },
        { key: "created_at-asc", sort: "id", order: "asc", label: this.$t("sorting.created_at_old") },
        { key: "created_at-desc", sort: "id", order: "desc", label: this.$t("sorting.created_at_new") },
      ];
    },
    perPageOptions() {
      return [10, 50, 100, 500, 1000, 2500].map((value) => ({
        id: value,
        name: String(value),
      }));
    },
    selectedPerPage: {
      get() {
        return this.perPageOptions.find((option) => option.id === Number(this.per_page))
            || this.perPageOptions[0];
      },
      set(value) {
        const nextPerPage = value && value.id ? Number(value.id) : 10;
        if (Number(this.per_page) === nextPerPage) {
          return;
        }
        this.per_page = nextPerPage;
        this.filterResult();
      },
    },
    user_header() {
      return JSON.parse(this.secureLocalStorage("user"));
    },
    shouldHideEmailField() {
      const companyId = this.filter?.company_id
          || this.activeItem?.company_id
          || this.resolvedUserDetail?.user?.company_id
          || this.user_header?.company_id;

      return Number(companyId) === 489;
    },
    onlineUsers() {
      return store.getters[_COMPANY_MODULE + "/" + ONLINE_USERS];
    },
    user_detail() {
      return store.getters[USER_DETAIL_MODULE + "/" + USER_DETAIL];
    },
    assignments() {
      return store.getters[ASSIGNMENT_MODULE + "/" + ASSIGNMENT_ITEM];
    },
    groups() {
      return store.getters[GROUP_MODULE + "/" + GROUP_ITEM];
    },
    items() {
      return store.getters[MODULE_NAME + "/" + ITEMS];
    },
    counts() {
      return store.getters[MODULE_NAME + "/" + COUNTS];
    },
    success() {
      return store.getters[MODULE_NAME + "/" + SUCCESS];
    },
    formattedItem() {
      let results = [];
      if (this.items === null || !("data" in this.items)) return [];
      this.pagination = this.items;
      this.items.data.forEach((item) => {
        results.push({
          id: item.id,
          name: item.name,
          surname: item.surname,
          dob: item.dob ? item.dob : "-",
          email: item.email,
          group_info: item.groups,
          company_id: item.company_id,
          company_name: item.company ? item.company.name : "-",
          corporate_id: item.corporate_id,
          corporate_name: item.corporate ? item.corporate.name : null,
          position_id: item.position_id,
          title_id: item.title_id,
          region_id: item.region_id,
          department_id: item.department_id,
          branch_id: item.branch_id,
          status_id: item.status_id,
          is_online: !!this.onlineUsers.find((onlineUser) => onlineUser.user == item.id && onlineUser.status),
          photo_link: item.photo_link,
          fullname: [item.name, item.surname].join(" "),
          title: item.title ? item.title.name : "-",
          login_check_field: item.login_check_field
              ? item.login_check_field
              : "-",
          created_at: item.created_at
              ? moment(item.created_at).format("DD/MM/YYYY HH:mm:ss")
              : "-",
          last_login_at: item.last_login_at
              ? moment(item.last_login_at).format("DD/MM/YYYY HH:mm:ss")
              : "-",
          phone: this.getPhoneNumber(item)
        });
      });

      return results;
    },
    resolvedUserDetail() {
      if (!this.activeItem || !this.user_detail || !this.profileDetailUserId) {
        return null;
      }

      return this.activeItem.id === this.profileDetailUserId
          ? this.user_detail
          : null;
    },
    selectedUsersCount() {
      let userCount = this.item_checked.length;
      return userCount;
    },
    // "Tümünü Seç" en az bir sayfada aktifse mail kapsamı sorulur
    isSelectAllApplied() {
      return this.checked_user_page.length > 0;
    },
    pendingMailTypeText() {
      if (this.pendingMailType === "forgot_password") {
        return this.$t("general.send_forgot_password_mail");
      }
      if (this.pendingMailType === "welcome") {
        return this.$t("general.send_welcome_mail");
      }
      return "";
    },
  },
  methods: {
    sendForgotPasswordMail() {
      this.requestMailSend("forgot_password");
    },
    sendWelcomeMail() {
      this.requestMailSend("welcome");
    },
    // "Tümünü Seç" kullanıldıysa mailin tüm kullanıcılara mı sadece seçililere mi
    // gideceği modalda sorulur; kullanılmadıysa doğrudan işaretli kullanıcılara gönderilir
    requestMailSend(mailType) {
      if (!this.item_checked || this.item_checked.length === 0) {
        return;
      }

      this.pendingMailType = mailType;

      if (this.isSelectAllApplied) {
        this.$refs.mailScopeModal.show();
        return;
      }

      this.sendMail(mailType, this.item_checked);
    },
    handleMailScopeSelected(scope) {
      const mailType = this.pendingMailType;

      if (!mailType) {
        return;
      }

      // 'all' -> sistemdeki tüm kullanıcılar, 'selected' -> sadece işaretli kullanıcı id'leri
      this.sendMail(mailType, scope === "all" ? "all" : this.item_checked);
    },
    sendMail(mailType, userIds) {
      const mailConfig = {
        forgot_password: {
          url: "/api/user/send-forgot-password-mail",
          allSkippedKey: "general.forgot_password_mail_all_skipped",
          queuedWithSkippedKey: "general.forgot_password_mail_queued_with_skipped",
          queuedKey: "general.forgot_password_mail_queued",
        },
        welcome: {
          url: "/api/user/send-welcome-mail",
          allSkippedKey: "general.welcome_mail_all_skipped",
          queuedWithSkippedKey: "general.welcome_mail_queued_with_skipped",
          queuedKey: "general.welcome_mail_queued",
        },
      }[mailType];

      if (!mailConfig) {
        return;
      }

      axios
          .post(mailConfig.url, {
            user_ids: userIds
          })
          .then((response) => {
            const queuedUserCount = response.data.queued_user_count || 0;
            const skippedUserCount = response.data.skipped_user_count || 0;

            this.successMessage = true;

            if (queuedUserCount === 0) {
              this.successMessageText = this.$t(mailConfig.allSkippedKey);
            } else if (skippedUserCount > 0) {
              this.successMessageText = this.$t(mailConfig.queuedWithSkippedKey, {
                queued: queuedUserCount,
                skipped: skippedUserCount,
              });
            } else {
              this.successMessageText = this.$t(mailConfig.queuedKey, {
                queued: queuedUserCount,
              });
            }

            this.clearUserSelection();
            this.scrollToTop();
          })
          .catch((error) => {
            console.error(error);
          })
          .finally(() => {
            this.pendingMailType = null;
          });
    },
    // İşaretli kullanıcıları ve "Tümünü Seç" durumunu birlikte sıfırlar
    clearUserSelection() {
      this.item_checked = [];
      this.checked_user_page = [];
      this.temp_page = [];
      this.selectAllUserText = this.$t("general.select_all");
    },
    getPhoneNumber(user) {
      return (!user.country_phone_code || !user.phone) ? '-' : `+${user.country_phone_code}${user.phone}`;
    },
    getUserStatus(status) {
      if (status === 1) {
        return { text: this.$t('general.active'), className: 'sf-table__status sf-table__status--success' }
      }
      return { text: this.$t('general.passive'), className: 'sf-table__status sf-table__status--danger' }
    },
    openUserAccount(event) {
      let UserInfo = {
        "target_user_id": event.id
      }
      axios.post('api/impersonate',
          UserInfo, {
            headers: this.apiHeaders,
          })
          .then((response) => {
            let token = ls.get("token");
            this.$router.push({ path: "/dashboard" });
            if (!ls.get("superAdminToken")) ls.set("superAdminToken", token);
            ls.set("token", response.data.access_token);
          })
    },
    allUserToggle() {
      let self = this;
      let found = this.checked_user_page.find(
          (element) => element == this.items.current_page
      );

      if (
          this.temp_page.find((r) => r === this.items.current_page) == undefined
      ) {
        this.formattedItem.forEach((item) => {
          $("#notification_checkbox_" + item.id).prop("checked", true);

          let checked_status = this.item_checked.find((r) => r === item.id);

          if (!checked_status) self.item_checked.push(item.id);
        });

        if (!found) {
          this.checked_user_page.push(this.items.current_page);
        }

        this.selectAllUserText = this.$t("general.deselect_all");
        this.temp_page.push(this.items.current_page);
      } else {
        this.formattedItem.forEach((item) => {
          $("#notification_checkbox_" + item.id).prop("checked", false);
          this.item_checked = this.item_checked.filter(function (checked_user) {
            return checked_user !== item.id;
          });
        });
        this.checked_user_page = this.checked_user_page.filter(function (
            checked_page
        ) {
          return checked_page !== found;
        });
        this.selectAllUserText = this.$t("general.select_all");
        this.temp_page.forEach((e, i) => {
          if (e == self.items.current_page) {
            self.temp_page.splice(i, 1);
          }
        });
      }
    },

    async deleteSelectedUsers() {
      let self = this;

      const isConfirmed = await confirmDialogDelete({ context: this });

      if (isConfirmed) {
        let payload = {
          users: self.item_checked
        };

        axios.post(`/api/user/bulk-destroy`, payload, {
          headers: this.apiHeaders,
        }).then((response) => {
          this.filterResult();
          self.item_checked = [];
          this.getUserDetail()
          this.getCounts()
        });
      }
    },
    passiveSelectedUsers() {
      let self = this;
      if (self.item_checked.length > 0) {
        self.item_checked.forEach((element) => {
          self.statusChange(element, 1);
        });
        self.item_checked = [];
      }
    },
    activeSelectedUsers() {
      let self = this;
      if (self.item_checked.length > 0) {
        self.item_checked.forEach((element) => {
          self.statusChange(element, 0);
        });
        self.item_checked = [];
      }
    },
    assignmentCreate(assignmentId) {
      this.assignment_loading = true;
      axios
          .get(`/api/attach-user-assignment/${this.activeUser}/${assignmentId}`, {
            headers: this.apiHeaders,
          })
          .then(() => {
            this.getAssignmentItem(this.activeUser);
            this.assignment_loading = false;
          });
    },
    groupCreate(groupId) {
      this.group_loading = true;
      axios
          .get(`/api/attach-user-group/${this.activeUser}/${groupId}`, {
            headers: this.apiHeaders,
          })
          .then(() => {
            this.getGroupItem(this.activeUser);
            this.group_loading = false;
          });
    },
    openAssignmentModal(userId) {
      this.activeUser = userId;
      this.assignment_loading = false;
      this.assignmentSearchQuery = '';
      this.getAssignmentItem(userId);
      this.assignmentModalOpen = true;
    },
    onAssignmentModalClose() {
      this.assignmentSearchQuery = '';
    },
    openPermissionModal() {
      UIkit.modal("#permission_modal").show();
    },
    openGroupModal(userId) {
      this.activeUser = userId;
      this.group_loading = true;
      this.getGroupItem(userId);
    },
    onGroupModalClose() {
      this.filterResult();
    },
    getCounts() {
      this.$store.dispatch(MODULE_NAME + "/" + GET_COUNTS, {
        url: BASE_URL + "/counts",
      });
    },
    // Listeleme ve Excel export'un aynı filtrelerle çalışması için
    // aktif filtreden (keepFilter) tek noktada istek parametreleri üretilir
    buildRequestFilters() {
      const activeFilter = this.keepFilter || {};

      let filters = {
        like: this.filter.search || null,
        sort: this.filter.sort,
        order: this.filter.order,
        with_groups: true,
        status_id: activeFilter.user_status ?? null,
        from_date: activeFilter.from_date ?? null,
        to_date: activeFilter.to_date ?? null,
        position_id: activeFilter.position_id ?? null,
        title_id: activeFilter.title_id ?? null,
        region_id: activeFilter.region_id ?? null,
        department_id: activeFilter.department_id ?? null,
        branch_id: activeFilter.branch_id ?? null,
      };

      if (activeFilter.company_id) {
        filters['company_id'] = activeFilter.company_id;
      }

      // {id, name} listesi tutan filtreler API için id dizisine çevrilir.
      // $.each yerine Object.entries: jQuery primitive string'de
      // `"length" in obj` deneyip TypeError atıyordu.
      Object.entries(activeFilter).forEach(([key, value]) => {
        if (Array.isArray(value) && key !== "company_id") {
          filters[key] = JSON.stringify(value.map((item) => item.id));
        }
      });

      return filters;
    },
    filterResult(filter = null, page = 1) {
      // Yeni filtre geldiyse sakla; gelmediyse (sayfalama, sıralama, yenileme)
      // en son uygulanan filtreyle devam et.
      // Yalnızca filtre NESNESİ kabul edilir: bu metot aynı zamanda debounce'lu
      // arama handler'ı olarak kullanılıyor ve oradan string gelebiliyor.
      if (filter && typeof filter === "object" && !Array.isArray(filter)) {
        this.keepFilter = filter;
      }

      const requestId = ++this.listRequestId;
      this.isFetchingList = true;

      return this.$store.dispatch(MODULE_NAME + "/" + GET_ITEMS, {
        url: BASE_URL,
        filters: {
          ...this.buildRequestFilters(),
          page: page,
          per_page: this.per_page,
        },
      }).finally(() => {
        if (requestId === this.listRequestId) {
          this.isFetchingList = false;
        }
      });
    },
    sortOrder({ order, sort }) {
      this.filter.order = order;
      this.filter.sort = sort;

      this.filterResult();
    },
    setPage(page) {
      let found = this.checked_user_page.find((element) => element == page);
      if (found) {
        this.selectAllUserText = this.$t("general.deselect_all");
      } else {
        this.selectAllUserText = this.$t("general.select_all");
      }

      // Yeni filtre gönderme; keepFilter'daki son filtreyle sadece sayfa değişsin
      this.filterResult(null, page);
    },
    scrollToTop() {
      window.scroll({
        top: 0,
        behavior: "smooth",
      });
    },
    statusChange(id, status_id) {
      store.dispatch(MODULE_NAME + "/" + UPDATE_USER, {
        url: BASE_URL + "/status/" + id,
        contents: {
          user: id,
          status_id: status_id,
        },
        returnType: "stateless",
        acceptPromise: true,
        _method: "put",
      }).then(() => {
        setTimeout(() => {
          this.getCounts();

        }, 100);
      });
    },
    async deleteUser(id) {
      const isConfirmed = await confirmDialogDelete({ context: this });

      if (isConfirmed) {
        axios.delete(`/api/user/${id}`, {
          headers: this.apiHeaders,
        })
            .then((response) => {
              this.filterResult();
            });
      }
    },
    getUserDetail(id, assignmentsPage = 1, keepModalOpen = false, updateUrl = true) {
      if (!id) {
        return Promise.resolve();
      }

      // Eğer zaten yükleme yapılıyorsa, tekrar istek atma
      if (this.isLoadingUserDetail) {
        return Promise.resolve();
      }

      this.isLoadingUserDetail = true;

      // Pagination durumunda requestId'yi artırma (component yeniden mount olmasın)
      const requestId = keepModalOpen ? this.profileRequestId : ++this.profileRequestId;

      if (!keepModalOpen) {
        this.profileDetailUserId = null;
        this.profileModalOpen = true;
      }

      // URL güncelleme gerekiyorsa
      if (updateUrl) {
        const query = { profile: id };

        // keepModalOpen ise mevcut assignmentsPage'i koru, değilse yeni bir profil açılıyor demektir
        if (keepModalOpen && assignmentsPage !== 1) {
          query.assignmentsPage = assignmentsPage;
        } else if (!keepModalOpen) {
          // Yeni profil açılırken URL'deki assignmentsPage parametresini temizle
          // Ancak URL'de varsa onu kullan
          const urlAssignmentsPage = parseInt(this.$route.query.assignmentsPage);
          if (urlAssignmentsPage && urlAssignmentsPage > 1) {
            query.assignmentsPage = urlAssignmentsPage;
            assignmentsPage = urlAssignmentsPage;
          }
        }

        // Mevcut URL parametreleri ile yeni parametreleri karşılaştır
        const currentProfileId = parseInt(this.$route.query.profile);
        const currentAssignmentsPage = parseInt(this.$route.query.assignmentsPage) || 1;
        const newAssignmentsPage = parseInt(query.assignmentsPage) || 1;

        const shouldUpdateUrl = currentProfileId !== id || currentAssignmentsPage !== newAssignmentsPage;

        if (shouldUpdateUrl) {
          this.$router.push({
            name: 'company.user.list',
            query: { ...this.$route.query, ...query }
          }).catch(() => {
          });
        }
      }

      return store.dispatch(USER_DETAIL_MODULE + "/" + GET_USER_DETAIL, {
        url: "api/user/detail-admin" + "/" + id,
        filters: { page: assignmentsPage, perPage: 15 }
      }).then(() => {
        if (!keepModalOpen) {
          this.profileModalOpen = true;
        }
        if (requestId === this.profileRequestId && this.activeItem && this.activeItem.id === id) {
          this.profileDetailUserId = id;
        }
      }).catch(error => {
        if (requestId === this.profileRequestId) {
          this.profileModalOpen = false;
          this.profileDetailUserId = null;
        }
        console.error('getUserDetail error:', error);
      }).finally(() => {
        this.isLoadingUserDetail = false;
      })
    },
    getAssignmentItem(userId, page = 1, search = '') {
      store.dispatch(ASSIGNMENT_MODULE + "/" + GET_ASSIGNMENT, {
        url: `api/user-assignments/${userId}`,
        filters: { page: page, per_page: 10, search: search }
      });
    },
    handleAssignmentPageChange(page) {
      if (this.activeUser) {
        this.getAssignmentItem(this.activeUser, page, this.assignmentSearchQuery);
      }
    },
    handleAssignmentSearch(searchQuery) {
      this.assignmentSearchQuery = searchQuery;
      if (this.activeUser) {
        this.getAssignmentItem(this.activeUser, 1, searchQuery);
      }
    },
    getGroupItem(userId) {
      store.dispatch(GROUP_MODULE + "/" + GET_GROUP, {
        url: `api/user-groups/${userId}`,
      }).then(() => {
        this.group_loading = false;
        this.groupModalOpen = true;
      }).catch(() => {
        showError(this.$t('errors.error_occurred'))
      }).finally(() => {
        this.group_loading = false;
      })
    },
    getFilter(value) {
      this.filter.company_id = value.company_id;

      this.filterResult(value);
    },
    // İndirme web/native ayrımı utils/fileDownload.js içinde yapılır;
    // burada sadece "ne indirilecek" tarif edilir.
    async exportUsers() {
      if (this.isReportDownloading) {
        return;
      }
      this.isReportDownloading = true;

      try {
        await downloadFromRequest({
          url: process.env.VUE_APP_BACKEND_APP_URL + "/api/user/export",
          method: "post",
          // Listede o an uygulanan filtrelerin aynısıyla export yapılır.
          // Filtreler query string yerine POST gövdesinde gider: uzun
          // filtrelerde (çok seçimli grup/unvan id listeleri) URL uzunluk
          // sınırına takılmaz.
          data: this.buildRequestFilters(),
          // Aynı filtrelerle tekrar indirildiğinde tarayıcı/proxy önbellekten
          // eski dosyayı dönmesin diye URL'e her istekte tekil bir değer
          // eklenir — SurveyResults.vue'daki `report` kalıbının aynısı.
          params: { report: Math.random() * 9999999 },
          headers: this.apiHeaders,
          fileName: exportFileName("users_list"),
        });
      } catch (error) {
        // Eskiden hata sessizce yutuluyordu. responseType 'blob' olduğu için
        // hata gövdesi de Blob gelir; readBlobError gerçek mesajı çıkarır.
        showError(await readBlobError(error));
      } finally {
        this.isReportDownloading = false;
      }
    },
    async openProfileModal(item) {
      if (!item) return;

      this.activeItem = item;

      // Sadece URL'yi güncelle, getUserDetail watch tarafından çağrılacak
      this.$router.push({
        name: 'company.user.list',
        query: { ...this.$route.query, profile: item.id }
      }).catch(() => {
      });
    },
    async openProfileModalFromUrl(userId) {
      // Önce formattedItem içinde kullanıcıyı ara
      let user = this.formattedItem.find(item => item.id === userId);

      // URL'den assignmentsPage parametresini al
      const assignmentsPage = parseInt(this.$route.query.assignmentsPage) || 1;

      if (user) {
        // Kullanıcı mevcut sayfada varsa normal şekilde aç
        this.activeItem = user;
        try {
          // URL zaten değişti, tekrar değiştirme
          await this.getUserDetail(user.id, assignmentsPage, false, false);
          this.profileModalOpen = true;
        } catch (e) {
          console.error(e);
        }
      } else {
        // Kullanıcı mevcut sayfada yoksa, minimal bir activeItem oluştur
        this.activeItem = { id: userId };
        try {
          // URL zaten değişti, tekrar değiştirme
          await this.getUserDetail(userId, assignmentsPage, false, false);
          this.profileModalOpen = true;
        } catch (e) {
          console.error('URL\'den profil açılırken hata:', e);
        }
      }
    },
    handleUserAssignmentPageChange(page) {
      if (this.activeItem && this.activeItem.id) {
        // Sadece URL'yi güncelle, getUserDetail watch tarafından çağrılacak
        this.$router.push({
          name: 'company.user.list',
          query: { ...this.$route.query, assignmentsPage: page }
        }).catch(() => {
        });
      }
    },
    closeModal() {
      this.profileModalOpen = false;
      this.activeItem = null;
      this.profileDetailUserId = null;

      // URL'den profile ve assignmentsPage query parametrelerini kaldır
      const query = { ...this.$route.query };
      delete query.profile;
      delete query.assignmentsPage;

      this.$router.push({
        name: 'company.user.list',
        query
      }).catch(() => {
      });
    }
  },
  async mounted() {
    this.isFetchingList = true;
    // company_admin değilse ve bir şirkete atanmışsa ilk listede sadece o şirket gelsin
    const corporateAccess = await getAssignedCorporateAccess();
    this.keepFilter = applyAssignedCorporateToFilter(this.keepFilter, corporateAccess);
    this.getCounts();
    await this.filterResult();

    if (this.$route.params.succesMessage != null) {
      this.successMessage = true;
      this.successMessageText = this.$route.params.succesMessage;
      this.scrollToTop();
    }

    // URL'de profile query parametresi varsa modal'ı aç
    if (this.$route.query.profile) {
      const userId = parseInt(this.$route.query.profile);
      if (userId) {
        this.openProfileModalFromUrl(userId);
      }
    }
  },
  watch: {
    success: function (val) {
      if (val) {
        this.filterResult();
      }
    },
    '$route.query.profile': function (newProfileId, oldProfileId) {
      if (newProfileId && newProfileId !== oldProfileId) {
        // Yeni profil ID'si varsa modal'ı aç
        const userId = parseInt(newProfileId);
        // Eğer activeItem zaten bu kullanıcıysa ve modal açıksa, tekrar açma
        if (userId && (!this.activeItem || this.activeItem.id !== userId || !this.profileModalOpen)) {
          this.openProfileModalFromUrl(userId);
        }
      } else if (!newProfileId && oldProfileId && this.profileModalOpen) {
        // Profil ID'si kaldırıldıysa (ör. tarayıcı geri tuşu) modal'ı kapat.
        //
        // State'i BURADA sıfırlamıyoruz: UIkit'in hide()'ı animasyonlu ve
        // asenkron. Hemen sıfırlarsak `v-if` UserProfile'ı hide bitmeden yok
        // eder, beforeDestroy içindeki $destroy(true) UIkit'in kapanış
        // akışını yarıda keser ve UIkit'in global "aktif modal" referansı
        // asılı kalır. Sonuç: bir sonraki kullanıcıya tıklanınca URL
        // değişir ama modal hiç açılmaz.
        //
        // Kapat butonu zaten hide() -> UIkit 'hidden' -> modalClosed ->
        // closeModal() yolunu izliyor; geri tuşu da aynı yola sokuluyor.
        const modalElement = document.getElementById('modal-profile');
        const modal = modalElement && UIkit.modal(modalElement);

        if (modal && modal.isToggled()) {
          modal.hide();
          return;
        }

        this.profileModalOpen = false;
        this.activeItem = null;
        this.profileDetailUserId = null;
      }
    },
    '$route.query.assignmentsPage': function (newPage, oldPage) {
      // Type-safe karşılaştırma için integer'a çevir
      const newPageNum = parseInt(newPage) || 0;
      const oldPageNum = parseInt(oldPage) || 0;

      // Assignment page değiştiğinde ve modal açıksa, verileri yenile
      if (newPageNum && newPageNum !== oldPageNum && this.profileModalOpen && this.activeItem) {
        // keepModalOpen = true, updateUrl = false (URL zaten değişti)
        this.getUserDetail(this.activeItem.id, newPageNum, true, false);
      }
    },
  },
  beforeDestroy() {
    if (this.debouncedFilterSearch) {
      this.debouncedFilterSearch.cancel();
    }
  },
};
</script>

<style scoped>
@import "~vue-multiselect/dist/vue-multiselect.min.css";

.users-list-page {
  --users-primary: var(--primaryColor, #3e416d);
  --users-text: #1e293b;
  --users-muted: #64748b;
  --users-border: #e2e8f0;
  --users-soft: #f6f7fb;
  min-width: 0;
}

.users-alert-card {
  margin-bottom: 16px;
  overflow: hidden;
  border: 1px solid var(--users-border);
  border-radius: 14px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.08);
}

.users-list-page .uk-alert {
  margin: 0;
}

.users-list-page .uk-alert p {
  margin: 0;
  color: #28a745;
  font-size: 0.875rem;
  line-height: 1.5;
}

.users-page-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 16px;
  margin-bottom: 16px;
}

.users-page-header .section-header-right > * {
  margin-left: 0;
}

.users-page-title {
  margin: 0;
  color: var(--users-text);
  font-size: 19px;
  font-weight: 600;
  line-height: 1.3;
}

.users-add-btn {
  padding: 10px 17px;
  border-radius: 10px;
  font-size: 13px;
  font-weight: 600;
  line-height: 17px;
  touch-action: manipulation;
  -webkit-tap-highlight-color: transparent;
}

.users-add-btn:active:not(:disabled) {
  transform: scale(0.98);
  opacity: 0.85;
}

.users-add-btn:disabled {
  opacity: 0.5;
  pointer-events: none;
}

.users-list-card {
  overflow: hidden;
  margin-top: 0;
  border: 1px solid var(--users-border);
  border-radius: 14px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.08);
}

.users-toolbar {
  padding: 16px;
}

.users-toolbar-inner {
  align-items: center;
  gap: 16px;
}

.users-toolbar-search {
  flex: 1 1 auto;
  min-width: 0;
  max-width: 480px;
}

.users-toolbar-inner ::v-deep .company-search-input,
.users-toolbar-inner ::v-deep .section-header-left {
  float: none;
  width: 100%;
  max-width: none;
  margin: 0;
}

.users-toolbar-inner ::v-deep .company-search-input input {
  height: auto;
  min-height: 0;
  padding: 10px 16px;
  font-size: 14px;
}

.users-toolbar-actions {
  display: flex;
  flex: 0 0 auto;
  align-items: center;
  gap: 8px;
}

.users-per-page-select {
  min-width: 110px;
  margin: 0 !important;
}

.users-list-page ::v-deep .users-per-page-select.form-group {
  margin-bottom: 0;
}

.users-toolbar-actions ::v-deep .btn-circle,
.users-toolbar-actions ::v-deep .users-icon-btn {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  touch-action: manipulation;
  -webkit-tap-highlight-color: transparent;
}

.users-toolbar-actions ::v-deep .btn-circle:active,
.users-toolbar-actions ::v-deep .users-icon-btn:active {
  transform: scale(0.98);
  opacity: 0.85;
}

.users-summary {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
  padding: 0 16px 16px;
}

.users-summary-stats {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
}

.users-summary-stats span,
.users-summary-selected {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  min-height: 32px;
  padding: 4px 12px;
  border-radius: 20px;
  background: var(--users-soft);
  color: var(--users-text);
  font-size: 14px;
  font-weight: 500;
  line-height: 1.4;
}

.users-bulk-actions {
  flex-wrap: wrap;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
  padding: 0 16px 16px;
}

.users-bulk-actions-left,
.users-bulk-actions-right {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 8px;
}

.users-bulk-actions-left {
  justify-content: flex-start;
}

.users-bulk-actions-right {
  margin-left: auto;
  justify-content: flex-end;
}

.users-bulk-actions .btn {
  border-radius: 10px;
  font-size: 13px;
  font-weight: 600;
  line-height: 17px;
}

/* Tablo tasarimi .sf-table'dan gelir (assets/scss/_data-table.scss).
   Burada yalnizca sayfaya ozgu degerler ayarlanir. */

.avatar-parent-child {
  width: max-content;
}

.users-permission-dialog {
  width: 50%;
}

.users-permission-body {
  padding-bottom: 32px;
}

.users-permission-body h4 {
  margin: 0 0 16px;
  font-size: 16px;
  font-weight: 600;
}

.users-pagination {
  padding: 0 8px 8px;
}

.multiselect__tag {
  color: white;
  padding-top: 0.5rem;
  padding-bottom: 0.5rem;
  background-color: #3e416d;
}

.multiselect__tag-icon {
  background-color: transparent;
  line-height: 2.2;
}

.multiselect__tag-icon:after {
  color: #fff;
}

.multiselect__input {
  border: unset !important;
}

.multiselect__tags .multiselect__input {
  border: unset !important;
}

input[type="button"],
input[type="submit"],
span.btn,
.btn {
  display: inline-block;
}

.bg-offline {
  background-color: #c0c0c0 !important;
}

@media (max-width: 959px) {
  .users-page-header {
    flex-direction: column;
    align-items: stretch;
    gap: 16px;
  }

  .users-page-header .section-header-left,
  .users-page-header .section-header-right {
    width: 100%;
  }

  .users-page-title {
    font-size: 24px;
    font-weight: 700;
    line-height: 1.3;
  }

  .users-list-page .uk-alert p {
    font-size: 16px;
  }

  .users-add-btn {
    width: 100%;
    min-height: 48px;
    padding: 12px 24px;
    font-size: 16px;
    font-weight: 600;
    line-height: 1.4;
  }

  .users-toolbar-inner ::v-deep .company-search-input input {
    height: 48px;
    min-height: 48px;
    padding: 12px 16px;
    font-size: 16px;
  }

  .users-toolbar-actions ::v-deep .btn-circle,
  .users-toolbar-actions ::v-deep .users-icon-btn {
    width: 48px;
    height: 48px;
    min-width: 48px;
    min-height: 48px;
  }

  .users-bulk-actions .btn {
    min-height: 48px;
    padding: 12px 16px;
    font-size: 16px;
    line-height: 1.4;
  }

  .users-permission-body h4 {
    font-size: 20px;
  }

  .users-toolbar-inner {
    flex-direction: column;
    align-items: stretch;
  }

  .users-toolbar-search {
    max-width: none;
    width: 100%;
  }

  .users-toolbar-actions {
    flex-wrap: wrap;
    width: 100%;
  }

  .users-per-page-select {
    flex: 1 1 110px;
    min-width: 110px;
  }

  /* 11 kolonlu tablo mobilde sikistirilmaz, yatay kaydirilir */
  .users-table-wrap {
    --sf-table-min-width: 1400px;
  }
}

@media (max-width: 767px) {
  .users-list-page {
    margin: 0;
    padding: 0 0 24px;
  }

  .users-page-title {
    font-size: 24px;
  }

  .users-list-card {
    border-radius: 14px;
  }

  .users-toolbar {
    padding: 16px;
  }

  .users-toolbar-actions {
    gap: 8px;
  }

  .users-list-page ::v-deep .users-per-page-select .multiselect,
  .users-list-page ::v-deep .users-per-page-select .multiselect__tags,
  .users-list-page ::v-deep .users-per-page-select .multiselect__select {
    height: 48px !important;
    min-height: 48px;
  }

  .users-list-page ::v-deep .users-per-page-select .multiselect__single,
  .users-list-page ::v-deep .users-per-page-select .multiselect__placeholder {
    margin: 0;
    font-size: 16px;
    line-height: 1.4;
  }

  .users-list-page ::v-deep .sort-dropdown .uk-dropdown-nav > li > a {
    display: flex;
    align-items: center;
    min-height: 48px;
    padding: 12px 16px;
    font-size: 16px;
    line-height: 1.4;
  }

  .users-list-page ::v-deep .large[uk-dropdown] {
    width: calc(100vw - 32px) !important;
    max-width: calc(100vw - 32px) !important;
    max-height: 70vh;
    overflow: auto;
    padding: 16px;
    border-radius: 16px;
  }

  .users-list-page ::v-deep .large[uk-dropdown] h4,
  .users-list-page ::v-deep .large[uk-dropdown] h5 {
    font-size: 16px;
    line-height: 1.4;
  }

  .users-list-page ::v-deep .large[uk-dropdown] .multiselect__tags,
  .users-list-page ::v-deep .large[uk-dropdown] .form-control,
  .users-list-page ::v-deep .large[uk-dropdown] input {
    min-height: 48px;
    font-size: 16px;
  }

  .users-list-page ::v-deep .large[uk-dropdown] .btn {
    min-height: 48px;
    border-radius: 10px;
    font-size: 16px;
  }

  .users-summary {
    flex-direction: column;
    align-items: stretch;
    padding: 0 16px 16px;
  }

  .users-summary-stats,
  .users-summary-selected {
    width: 100%;
  }

  .users-summary-stats span,
  .users-summary-selected {
    min-height: 40px;
    font-size: 14px;
  }

  .users-bulk-actions {
    flex-direction: column;
    align-items: stretch;
    padding: 0 16px 16px;
  }

  .users-bulk-actions-left,
  .users-bulk-actions-right {
    width: 100%;
  }

  /* Uzun etiketli toplu islemler alt alta kalir */
  .users-bulk-actions-left {
    flex-direction: column;
  }

  /* "Tumunu sec" + "Secilileri sil" AYNI satirda, esit paylasimli */
  .users-bulk-actions-right {
    flex-direction: row;
    flex-wrap: nowrap;
    gap: 8px;
    margin-left: 0;
  }

  .users-bulk-actions .btn {
    display: inline-flex;
    width: 100%;
    min-height: 48px;
    align-items: center;
    justify-content: center;
    gap: 6px;
    margin: 0;
    /* MOBILE_UI_GUIDELINES: Body 16px, line-height x1.4 */
    font-size: 16px;
    line-height: 1.4;
    text-align: center;
  }

  .users-bulk-actions-right .btn {
    flex: 1 1 0;
    width: auto;
    min-width: 0;
    padding: 10px 12px;
    /* MOBILE_UI_GUIDELINES: Label 14px. Yan yana iki buton 375px ekranda
       ~167px'e dusuyor; 16px etiket tasiyor. */
    font-size: 14px;
    /* bootstrap .btn nowrap veriyor; dar butonda etiket sarabilmeli */
    white-space: normal;
  }

  .users-bulk-actions-right .btn i {
    flex-shrink: 0;
    font-size: 16px;
  }

  .users-bulk-actions .btn:active {
    transform: scale(0.98);
    opacity: 0.85;
  }

  .users-bulk-actions .btn:disabled {
    opacity: 0.5;
    pointer-events: none;
  }

  .users-permission-dialog {
    width: calc(100vw - 32px);
    max-width: calc(100vw - 32px);
    margin: 16px auto;
    border-radius: 16px 16px 0 0;
  }

  .users-permission-body {
    padding-bottom: 24px;
  }

  /* yatay kaydirmayi .sf-table sarmalayicisi yapar */
  .users-permission-table {
    --sf-table-min-width: 480px;
  }

  .users-pagination {
    padding: 0 16px 16px;
  }
}

@media (hover: hover) and (pointer: fine) {
  .users-add-btn:hover:not(:disabled) {
    opacity: 0.92;
    cursor: pointer;
  }

  .multiselect__tag-icon:hover {
    background-color: rgba(0, 0, 0, 0.1);
  }
}

@media (hover: none) {
  .users-list-page ::v-deep .btn-hover:hover {
    background-color: transparent;
  }
}

@media (prefers-reduced-motion: reduce) {
  .users-add-btn:active:not(:disabled),
  .users-bulk-actions .btn:active,
  .users-toolbar-actions ::v-deep .btn-circle:active,
  .users-toolbar-actions ::v-deep .users-icon-btn:active {
    transform: none;
  }
}
</style>
